import { useCallback, useLayoutEffect, useRef, useState } from 'react'

export type FullPhase = 'idle' | 'entering' | 'revealing'

/**
 * Expand / mode-switch choreography (compact list/stack ↔ full).
 *
 * A list↔stack switch keeps `isCompactMode` true on both sides, so no phase
 * runs — both layouts share the compact titlebar and the OS resize is a plain
 * height snap.
 *
 * On Electron the OS window resizes asynchronously, so anything full-mode that
 * paints before the resize settles would briefly show at the wrong (cramped)
 * size — the old list at top-left, a borderless card outline, a stray tab. We
 * hold the whole full UI hidden across the resize and reveal it once settled:
 *
 * - `'entering'`  — body opacity 0, union shape + tab bar hidden, while the
 *                   window resizes. The 'full' resize IPC is itself deferred
 *                   (WindowModeProvider) so this hidden state paints first.
 * - `'revealing'` — once the resize settles, fade the full UI in. Cleared to
 *                   `'idle'` via `onBodyRevealEnd` when the body fade ends.
 *
 * The titlebar slot is hidden across the settle in BOTH directions (so tabs
 * don't drift / the compact bar doesn't pop early).
 *
 * The union shape classes (`entering-full` / `revealing-full`) are mirrored
 * onto <html> because the slots live at window-frame level, outside
 * `.main-layout`.
 */
export function useModeSwitchChoreography(
  isCompactMode: boolean,
  isElectron: boolean,
  tabBarSlotRef: React.RefObject<HTMLDivElement | null>,
): { fullPhase: FullPhase; onBodyRevealEnd: () => void } {
  const [fullPhase, setFullPhase] = useState<FullPhase>('idle')
  const prevSlotModeRef = useRef(isCompactMode)

  useLayoutEffect(() => {
    if (prevSlotModeRef.current === isCompactMode) return
    const enteringFull = prevSlotModeRef.current && !isCompactMode
    prevSlotModeRef.current = isCompactMode

    // Web: the mode switch is a pure CSS swap — the browser window never
    // resizes, so no resize event ever fires and the settle wait below would
    // leave the titlebar slot hidden until the 700ms fallback (and the full
    // UI at opacity 0 on the way back). Skip the wait, keep the fades: the
    // slot is never hidden, and entering full starts the same 'revealing'
    // fade Electron plays after its resize settles — just immediately.
    // (Entering list needs no phase — the list fade is plain CSS on
    // .main-layout--list-mode.)
    if (!isElectron) {
      setFullPhase(enteringFull ? 'revealing' : 'idle')
      return
    }

    // Hide the titlebar slot (tab / compact bar) across the settle in both directions.
    const slot = tabBarSlotRef.current
    if (slot) slot.style.visibility = 'hidden'
    // Entering full: hold the body hidden until settle, then fade it in. (The union
    // shape is gated separately on fullPhase, so it stays hidden through the fade.)
    setFullPhase(enteringFull ? 'entering' : 'idle')

    let settleTimer = 0
    let fallbackTimer = 0
    let revealed = false
    const reveal = () => {
      // Both the debounced settle AND the fallback call this — guard so it runs
      // exactly once. Without it the fallback re-fires after the fade finished,
      // re-arming 'revealing' and replaying the whole fade ("fades out + in again").
      if (revealed) return
      revealed = true
      clearTimeout(settleTimer)
      clearTimeout(fallbackTimer)
      window.removeEventListener('resize', onResize)
      if (tabBarSlotRef.current) tabBarSlotRef.current.style.visibility = ''
      if (enteringFull) setFullPhase('revealing')
    }
    const onResize = () => {
      clearTimeout(settleTimer)
      settleTimer = window.setTimeout(reveal, 120)
    }
    window.addEventListener('resize', onResize)
    fallbackTimer = window.setTimeout(reveal, 700)
    return () => {
      clearTimeout(settleTimer)
      clearTimeout(fallbackTimer)
      window.removeEventListener('resize', onResize)
      // Bewusst der AKTUELLE Knoten: Sichtbar werden muss die Leiste, die jetzt dasteht.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (tabBarSlotRef.current) tabBarSlotRef.current.style.visibility = ''
    }
  }, [isCompactMode, isElectron, tabBarSlotRef])

  // The content card's union shape (border + bump) is rendered OUTSIDE .main-layout,
  // so drive it via global html classes synced to the phase: hidden while entering,
  // then faded in together with the body while revealing — so the border never
  // shows around an empty card. Layout effect so the class is set before paint.
  useLayoutEffect(() => {
    const html = document.documentElement.classList
    html.toggle('entering-full', fullPhase === 'entering')
    html.toggle('revealing-full', fullPhase === 'revealing')
  }, [fullPhase])

  // Clear the fade phase once the body's own reveal animation ends.
  const onBodyRevealEnd = useCallback(() => setFullPhase('idle'), [])

  return { fullPhase, onBodyRevealEnd }
}
